import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/client'
import { ProductCard } from '../components/products'
import { Loading } from '../components/ui'
import { ProductsSEO } from '../hooks/useSEO'
import type { Product } from '../types'
import { productCategoriesFilter, productPlatformsFilter } from '../types'
import { useAuthStore } from '../stores/authStore'

interface ProductsResponse {
  products: Product[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function ProductsPage() {
  const { isAuthenticated } = useAuthStore()
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    category: '',
    platform: '',
    search: '',
    page: 1,
  })
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchProducts()
  }, [filters])

  const fetchProducts = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters.category) params.append('category', filters.category)
      if (filters.platform) params.append('platform', filters.platform)
      if (filters.search) params.append('search', filters.search)
      params.append('page', String(filters.page))
      params.append('limit', '12')

      const data = await api.get<ProductsResponse>(`/products?${params}`)
      setProducts(data.products)
      setTotalPages(data.totalPages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <ProductsSEO />
      <div className="page-header">
        <div>
          <h1>Product Marketplace</h1>
          <p className="text-secondary">Browse skills, prompts, and tools for AI agents</p>
        </div>
        {isAuthenticated && (
          <Link to="/products/new" className="btn btn-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            List a Product
          </Link>
        )}
      </div>

      <div className="card-static" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="input"
            placeholder="Search products..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            style={{ flex: 1, minWidth: 200 }}
          />
          <select
            className="input select"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
            style={{ width: 180 }}
          >
            {productCategoriesFilter.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          <select
            className="input select"
            value={filters.platform}
            onChange={(e) => setFilters({ ...filters, platform: e.target.value, page: 1 })}
            style={{ width: 180 }}
          >
            {productPlatformsFilter.map((plat) => (
              <option key={plat.value} value={plat.value}>
                {plat.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {isLoading ? (
        <Loading message="Loading products..." />
      ) : products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          </div>
          <h3>No products found</h3>
          <p>Try adjusting your filters or check back later</p>
        </div>
      ) : (
        <>
          <div className="jobs-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={filters.page === 1}
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              >
                &lt;
              </button>
              {(() => {
                // Sliding window pagination: show 5 pages centered around current page
                const maxVisible = 5
                let start = Math.max(1, filters.page - Math.floor(maxVisible / 2))
                const end = Math.min(totalPages, start + maxVisible - 1)
                // Adjust start if we're near the end
                start = Math.max(1, end - maxVisible + 1)

                return Array.from({ length: end - start + 1 }, (_, i) => {
                  const pageNum = start + i
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${filters.page === pageNum ? 'active' : ''}`}
                      onClick={() => setFilters({ ...filters, page: pageNum })}
                    >
                      {pageNum}
                    </button>
                  )
                })
              })()}
              <button
                className="pagination-btn"
                disabled={filters.page === totalPages}
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              >
                &gt;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
